import { useState } from 'react';
import { Task, PERMISSIONS } from '@property/shared';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Navigation,
  FileText,
  CheckCircle2,
  ClipboardCheck,
  UserRoundCog,
  MapPin,
  FileCheck,
} from 'lucide-react';
import { useProperty } from '@/hooks/use-property-queries';
import { usePermissions } from '@/hooks/use-permissions';
import { usePendingReviews, useTaskInspection } from '@/hooks/use-inspection-queries';
import { useUserProfile } from '@/hooks/use-task-queries';
import { useNavigate } from '@tanstack/react-router';
import { formatDate, formatTimestamp } from '@/lib/date';
import { StaticMap } from '@/components/static-map';
import { ReassignTaskModal } from '@/components/reassign-task-modal';
import { ViewJumpNav, type JumpLink } from '@/components/view-jump-nav';
import { appUrls } from '@/lib/app-urls';

interface TaskDetailViewProps {
  task: Task;
}

export function TaskDetailView({ task }: TaskDetailViewProps) {
  const { data: property, isLoading: propertyLoading } = useProperty(task.entityId);
  const navigate = useNavigate();
  const { can, userId } = usePermissions();
  const canSubmitInspection = can(PERMISSIONS.INSPECTIONS_SUBMIT) && task.assignedTo === userId;
  const isReviewer = can(PERMISSIONS.INSPECTIONS_REVIEW);
  const canReview = isReviewer && task.type === 'REVIEW';
  const canReassign = can(PERMISSIONS.TASKS_ASSIGN) && task.status !== 'COMPLETED';
  const canViewProperties = can(PERMISSIONS.PROPERTIES_READ);
  const canViewInspections = can(PERMISSIONS.INSPECTIONS_READ);
  const [showReassign, setShowReassign] = useState(false);

  const jumpLinks: JumpLink[] = [
    ...(task.artifactId && canViewInspections
      ? [{ label: 'Inspection Report', icon: FileCheck, to: appUrls.inspection(task.artifactId) }]
      : []),
    ...(canViewProperties
      ? [{ label: 'Property', icon: MapPin, to: appUrls.propertyMonitoring(task.entityId) }]
      : []),
  ];

  const { data: pendingReviews } = usePendingReviews({ viewPage: 1, enabled: canReview });
  const { data: taskInspection } = useTaskInspection(task.id, {
    enabled: task.status === 'COMPLETED' && task.type === 'LAND_MONITORING',
  });
  const { data: assignedToUser } = useUserProfile(task.assignedTo);
  const { data: reviewerUser } = useUserProfile(taskInspection?.reviewedBy);
  const linkedInspection = canReview
    ? pendingReviews?.find((i) => i.propertyId === task.entityId)
    : undefined;

  const handleStartReport = () => {
    console.log('Navigating to report for task:', task.id);
    try {
      navigate({
        to: '/dashboard/task/$taskId/report',
        params: { taskId: task.id },
      });
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  const handleGetDirections = () => {
    const address = `${task.location.line1}, ${task.location.city}, ${task.location.state}, ${task.location.pincode}, India`;
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    window.open(mapsUrl, '_blank');
  };

  const getStatusLabel = (status: Task['status']) => {
    return status
      .split('_')
      .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
      .join(' ');
  };

  const getTaskTypeLabel = (type: Task['type']) => {
    return type
      .split('_')
      .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className="w-full">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Property Map - Large */}
          {propertyLoading ? (
            <Skeleton className="w-full h-80 rounded-lg" />
          ) : property?.accessPin ? (
            <StaticMap center={property.accessPin} zoom={14} className="w-full h-80" />
          ) : null}

          {/* Task Title and Location */}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold tracking-tight">{task.title}</h1>
            <p className="text-sm text-muted-foreground">
              {task.location.line1}
              {task.location.line2 ? `, ${task.location.line2}` : ''}, {task.location.city},{' '}
              {task.location.state}
            </p>
          </div>

          {/* Jump navigation */}
          <ViewJumpNav links={jumpLinks} />

          {/* Task Details Section */}
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Task Details
            </h2>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-sm text-muted-foreground">Task ID</span>
                <span className="text-sm font-mono text-primary">{task.id}</span>
              </div>

              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-sm text-muted-foreground">Task Type</span>
                <span className="text-sm font-medium text-right">
                  {getTaskTypeLabel(task.type)}
                </span>
              </div>

              <div className="flex items-center justify-between py-3 border-b">
                <span className="text-sm text-muted-foreground">Status</span>
                <span className="text-sm font-medium text-right">
                  {getStatusLabel(task.status)}
                </span>
              </div>

              {!task.completed && (
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-sm text-muted-foreground">Due Date</span>
                  <span className="text-sm font-medium text-right">{formatDate(task.before)}</span>
                </div>
              )}

              {task.assignedTo && (
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-sm text-muted-foreground">Assigned To</span>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      {assignedToUser?.profilePictureUrl && (
                        <AvatarImage
                          src={assignedToUser.profilePictureUrl}
                          alt={assignedToUser.name ?? ''}
                        />
                      )}
                      <AvatarFallback className="text-xs">
                        {(assignedToUser?.name ?? task.assignedTo)
                          .split(' ')
                          .map((w) => w[0])
                          .slice(0, 2)
                          .join('')
                          .toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-right">
                      {assignedToUser?.name ?? task.assignedTo}
                    </span>
                  </div>
                </div>
              )}

              {task.completed && (
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-sm text-muted-foreground">Completed Date</span>
                  <span className="text-sm font-medium text-right flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    {formatTimestamp(task.completed)}
                  </span>
                </div>
              )}

              {task.status === 'COMPLETED' && taskInspection?.reviewedBy && (
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-sm text-muted-foreground">Reviewed By</span>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      {reviewerUser?.profilePictureUrl && (
                        <AvatarImage
                          src={reviewerUser.profilePictureUrl}
                          alt={reviewerUser.name ?? ''}
                        />
                      )}
                      <AvatarFallback className="text-xs">
                        {(reviewerUser?.name ?? taskInspection!.reviewedBy!)
                          .split(' ')
                          .map((w) => w[0])
                          .slice(0, 2)
                          .join('')
                          .toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-right">
                      {reviewerUser?.name ?? taskInspection!.reviewedBy}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Property Information Section */}
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Property Details
            </h2>

            {propertyLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : property ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-sm text-muted-foreground">Property Type</span>
                  <span className="text-sm font-medium">
                    {property.type
                      .split('_')
                      .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(' ')}
                  </span>
                </div>

                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-sm text-muted-foreground">Total Area</span>
                  <span className="text-sm font-medium">
                    {property.area.value}{' '}
                    {property.area.unit === 'acre'
                      ? 'Acres'
                      : property.area.unit === 'sqft'
                        ? 'Sq. Ft'
                        : 'Sq. Yards'}
                  </span>
                </div>

                <div className="flex items-center justify-between py-3 border-b">
                  <span className="text-sm text-muted-foreground">Property ID</span>
                  <span className="text-sm font-mono text-primary">{property.propertyId}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4">Property information unavailable</p>
            )}
          </div>

          {canReassign && (
            <div className="pt-4">
              <Button
                size="lg"
                variant="outline"
                className="h-12 w-full text-base"
                onClick={() => setShowReassign(true)}
              >
                <UserRoundCog className="mr-2 h-5 w-5" />
                Reassign
              </Button>
            </div>
          )}

          {/* Action Buttons — only for agents who can submit inspections. */}
          {canSubmitInspection && task.status === 'ASSIGNED' && (
            <div className="space-y-3 pt-4">
              <Button
                size="lg"
                variant="outline"
                className="h-12 w-full text-base"
                onClick={handleGetDirections}
              >
                <Navigation className="mr-2 h-5 w-5" />
                Get Directions
              </Button>

              <Button
                size="lg"
                variant="action"
                className="h-12 w-full text-base"
                onClick={handleStartReport}
              >
                <FileText className="mr-2 h-5 w-5" />
                Start
              </Button>
            </div>
          )}

          {canSubmitInspection && task.status === 'IN_PROGRESS' && (
            <div className="pt-4">
              <Button
                size="lg"
                variant="action"
                className="h-12 w-full text-base"
                onClick={handleStartReport}
              >
                <FileText className="mr-2 h-5 w-5" />
                Continue
              </Button>
            </div>
          )}

          {/* View Report — agents on a completed LAND_MONITORING task. */}
          {task.status === 'COMPLETED' &&
            task.type === 'LAND_MONITORING' &&
            taskInspection &&
            !isReviewer && (
              <div className="pt-4">
                <Button
                  size="lg"
                  variant="action"
                  className="h-12 w-full text-base"
                  onClick={() =>
                    navigate({
                      to: '/dashboard/inspection/$inspectionId',
                      params: { inspectionId: taskInspection.inspectionId },
                    })
                  }
                >
                  <FileText className="mr-2 h-5 w-5" />
                  View Report
                </Button>
              </div>
            )}

          {/* Review — admins/reviewers on a completed LAND_MONITORING task with a pending inspection. */}
          {isReviewer &&
            task.type === 'LAND_MONITORING' &&
            task.status === 'COMPLETED' &&
            taskInspection &&
            taskInspection.status !== 'completed' && (
              <div className="pt-4">
                <Button
                  size="lg"
                  variant="action"
                  className="h-12 w-full text-base"
                  onClick={() =>
                    navigate({
                      to: '/dashboard/inspection/$inspectionId',
                      params: { inspectionId: taskInspection.inspectionId },
                    })
                  }
                >
                  <ClipboardCheck className="mr-2 h-5 w-5" />
                  Review
                </Button>
              </div>
            )}

          {/* View — admins/reviewers on a completed LAND_MONITORING task once the inspection is approved. */}
          {isReviewer &&
            task.type === 'LAND_MONITORING' &&
            taskInspection?.status === 'completed' && (
              <div className="pt-4">
                <Button
                  size="lg"
                  variant="action"
                  className="h-12 w-full text-base"
                  onClick={() =>
                    navigate({
                      to: '/dashboard/inspection/$inspectionId',
                      params: { inspectionId: taskInspection.inspectionId },
                    })
                  }
                >
                  <FileText className="mr-2 h-5 w-5" />
                  View
                </Button>
              </div>
            )}

          {/* Review button — only for principals who can review inspections on a REVIEW task. */}
          {canReview && task.status !== 'COMPLETED' && (
            <div className="pt-4">
              <Button
                size="lg"
                className="h-12 w-full text-base"
                disabled={!linkedInspection}
                onClick={() =>
                  linkedInspection &&
                  navigate({
                    to: '/dashboard/inspection/$inspectionId',
                    params: { inspectionId: linkedInspection.inspectionId },
                  })
                }
              >
                <ClipboardCheck className="mr-2 h-5 w-5" />
                {linkedInspection ? 'Review' : 'No Pending Inspection'}
              </Button>
            </div>
          )}
        </div>
      </div>
      <ReassignTaskModal
        task={showReassign ? task : null}
        open={showReassign}
        onOpenChange={setShowReassign}
      />
    </div>
  );
}
